import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Crown, 
  Clock, 
  Award, 
  Star, 
  CheckCircle, 
  BookOpen, 
  Globe,
  Mail,
  ArrowRight,
  Target,
  Shield,
  TrendingUp,
  Users,
  Building,
  FileCheck,
  Briefcase,
  Home,
  ArrowLeft
} from "lucide-react";

interface EmbedCCSPProps {
  compact?: boolean;
  showHero?: boolean;
  showQuickFacts?: boolean;
  showLearningAreas?: boolean;
  showEligibility?: boolean;
  showFramework?: boolean;
  showApplication?: boolean;
  showIdealFor?: boolean;
  showNextSteps?: boolean;
  maxWidth?: string;
}

const EmbedCCSP = ({ 
  compact = false,
  showHero = true,
  showQuickFacts = true,
  showLearningAreas = true,
  showEligibility = true,
  showFramework = true,
  showApplication = true,
  showIdealFor = true,
  showNextSteps = true,
  maxWidth = "6xl"
}: EmbedCCSPProps) => {
  const keyAreas = [
    {
      icon: <Crown className="w-6 h-6" />,
      title: "Elite-Level Leadership",
      description: "Premier designation recognizing the highest level of converged security expertise"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Strategic Integration",
      description: "Proven ability to integrate physical, cyber, and operational security domains"
    },
    {
      icon: <Building className="w-6 h-6" />,
      title: "Enterprise Implementation",
      description: "Demonstrated leadership in implementing enterprise-wide converged security frameworks"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Cross-Functional Collaboration",
      description: "Expertise in leading collaboration across IT, OT, compliance, and physical security"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Framework Mastery",
      description: "Advanced implementation of ISO 31000, ISO 27001, ISO 22301, GDPR, and NIST standards"
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Operational Excellence",
      description: "Proven track record in deploying Zero Trust Architecture and SASE frameworks"
    }
  ];

  const eligibilityCriteria = [
    {
      title: "Valid Certifications",
      description: "Hold current CSCE, CSP, and CSL certifications",
      icon: <Award className="w-5 h-5 text-purple-600" />
    },
    {
      title: "Leadership Experience", 
      description: "Minimum 5 years in managerial/leadership role",
      icon: <Users className="w-5 h-5 text-purple-600" />
    },
    {
      title: "Active CSP Role",
      description: "Demonstrate active fulfillment of Converged Security Practitioner role",
      icon: <Briefcase className="w-5 h-5 text-purple-600" />
    },
    {
      title: "Fellowship Status",
      description: "Active holder of fellowship or chartered status from recognized institution",
      icon: <Crown className="w-5 h-5 text-purple-600" />
    }
  ];

  const frameworkRequirements = [
    "Strategic integration of physical, cyber, and operational domains",
    "Cross-functional collaboration across IT, OT, compliance, and physical security",
    "Adoption of ISO 31000, ISO 27001, ISO 22301, GDPR, and NIST standards",
    "Integration of SIEM, PSIM, IAM, and open architecture systems",
    "Maintenance of unified risk register and shared KPIs",
    "Deployment of Zero Trust Architecture and SASE frameworks"
  ];

  const idealFor = [
    "Senior security executives with comprehensive certification portfolio",
    "Chief Security Officers implementing enterprise frameworks",
    "Security consultants with proven implementation experience",
    "Risk management leaders with chartered professional status",
    "Enterprise architects designing converged security solutions",
    "Strategic advisors driving organizational security transformation"
  ];

  return (
    <div className="bg-gradient-to-br from-purple-50 to-indigo-50 font-sans relative">
      {/* Navigation Bar - Upper Right */}
      <div className="absolute top-4 right-6 flex gap-2 z-10">
        <Button 
          variant="outline" 
          size="sm"
          className="bg-white border-gray-300 hover:bg-gray-50"
          onClick={() => window.history.back()}
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <Button 
          variant="outline" 
          size="sm"
          className="bg-white border-gray-300 hover:bg-gray-50"
          onClick={() => window.open('https://convergedsecurity.es/', '_blank')}
        >
          <Home className="w-4 h-4 mr-1" />
          Home
        </Button>
      </div>
      {/* Hero Section */}
      {showHero && (
        <div className="bg-gradient-to-r from-purple-700 to-indigo-800 text-white py-12">
          <div className={`max-w-${maxWidth} mx-auto px-4`}>
            <div className={`grid ${compact ? 'lg:grid-cols-1 text-center' : 'lg:grid-cols-2'} gap-8 items-center`}>
              <div>
                <Badge className="bg-white/20 text-white border-white/30 mb-3">
                  Premier / Chartered Designation
                </Badge>
                <h1 className={`${compact ? 'text-3xl md:text-4xl' : 'text-4xl md:text-5xl'} font-bold mb-4`}>
                  C-CSP
                </h1>
                <h2 className={`${compact ? 'text-xl md:text-2xl' : 'text-2xl md:text-3xl'} font-semibold mb-3 text-purple-100`}>
                  Chartered Converged Security Practitioner
                </h2>
                <p className={`${compact ? 'text-base' : 'text-lg'} text-purple-100 mb-6 leading-relaxed`}>
                  The premier designation recognizing elite-level leadership and operational excellence.
                </p>
                {!compact && (
                  <div className="flex flex-wrap gap-3">
                    <Button 
                      size={compact ? "default" : "lg"} 
                      className="bg-white text-purple-700 hover:bg-purple-50"
                      onClick={() => window.open('https://pay.sumup.com/b2c/QVSOSICX', '_blank')}
                    >
                      <Star className="w-4 h-4 mr-2" />
                      Pay Application - €50
                    </Button>
                    <Button 
                      size={compact ? "default" : "lg"} 
                      variant="outline" 
                      className="border-white text-white hover:bg-white/10"
                      onClick={() => window.open('https://pay.sumup.com/b2c/QMN044J9', '_blank')}
                    >
                      <Award className="w-4 h-4 mr-2" />
                      Pay Final - €450
                    </Button>
                  </div>
                )}
              </div>
              {!compact && (
                <div className="flex justify-center">
                  <div className="relative">
                    <div className="w-60 h-60 rounded-full bg-gradient-to-br from-purple-600 to-indigo-700 flex items-center justify-center drop-shadow-2xl">
                      <Crown className="w-24 h-24 text-white" />
                    </div>
                    <div className="absolute -inset-4 bg-gradient-to-r from-purple-400/20 to-indigo-400/20 rounded-full blur-xl"></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quick Facts */}
      {showQuickFacts && (
        <div className={`${compact ? 'py-8' : 'py-12'} bg-white`}>
          <div className={`max-w-${maxWidth} mx-auto px-4`}>
            <div className={`grid ${compact ? 'grid-cols-2 md:grid-cols-4' : 'md:grid-cols-4'} gap-4`}>
              <Card className="text-center border-purple-200">
                <CardContent className={compact ? "pt-4" : "pt-6"}>
                  <Star className={`${compact ? 'w-6 h-6' : 'w-8 h-8'} text-purple-600 mx-auto mb-2`} />
                  <h3 className={`font-bold ${compact ? 'text-lg' : 'text-2xl'} text-gray-900`}>€500</h3>
                  <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-600`}>Total Fee</p>
                </CardContent>
              </Card>
              <Card className="text-center border-purple-200">
                <CardContent className={compact ? "pt-4" : "pt-6"}>
                  <Clock className={`${compact ? 'w-6 h-6' : 'w-8 h-8'} text-purple-600 mx-auto mb-2`} />
                  <h3 className={`font-bold ${compact ? 'text-lg' : 'text-2xl'} text-gray-900`}>3 Years</h3>
                  <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-600`}>Chartered Award</p>
                </CardContent>
              </Card>
              <Card className="text-center border-purple-200">
                <CardContent className={compact ? "pt-4" : "pt-6"}>
                  <Users className={`${compact ? 'w-6 h-6' : 'w-8 h-8'} text-purple-600 mx-auto mb-2`} />
                  <h3 className={`font-bold ${compact ? 'text-sm' : 'text-xl'} text-gray-900`}>5+ Years</h3>
                  <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-600`}>Leadership Experience</p>
                </CardContent>
              </Card>
              <Card className="text-center border-purple-200">
                <CardContent className={compact ? "pt-4" : "pt-6"}>
                  <FileCheck className={`${compact ? 'w-6 h-6' : 'w-8 h-8'} text-purple-600 mx-auto mb-2`} />
                  <h3 className={`font-bold ${compact ? 'text-lg' : 'text-2xl'} text-gray-900`}>30 Days</h3>
                  <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-600`}>Review Period</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}

      {/* Key Competencies */}
      {showLearningAreas && (
        <div className={compact ? 'py-8' : 'py-12'}>
          <div className={`max-w-${maxWidth} mx-auto px-4`}>
            <div className="text-center mb-8">
              <h2 className={`${compact ? 'text-2xl md:text-3xl' : 'text-3xl md:text-4xl'} font-bold text-gray-900 mb-3`}>
                Elite-Level Expertise
              </h2>
              <p className={`${compact ? 'text-base' : 'text-lg'} text-gray-600 max-w-2xl mx-auto`}>
                C-CSP represents the pinnacle of converged security achievement
              </p>
            </div>
            
            <div className={`grid ${compact ? 'md:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-3'} gap-6`}>
              {keyAreas.slice(0, compact ? 4 : 6).map((area, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow duration-300 border-purple-100">
                  <CardHeader className={compact ? "pb-2" : "pb-3"}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`p-2 bg-purple-100 rounded-lg text-purple-600`}>
                        {React.cloneElement(area.icon, { 
                          className: compact ? "w-4 h-4" : "w-6 h-6" 
                        })}
                      </div>
                      <CardTitle className={compact ? "text-base" : "text-lg"}>{area.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className={compact ? "pt-0" : ""}>
                    <p className={`${compact ? 'text-sm' : 'text-base'} text-gray-600`}>{area.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Eligibility Criteria */}
      {showEligibility && (
        <div className={`${compact ? 'py-8' : 'py-12'} bg-gradient-to-r from-purple-700 to-indigo-800 text-white`}>
          <div className={`max-w-${maxWidth} mx-auto px-4`}>
            <div className="text-center mb-8">
              <h2 className={`${compact ? 'text-2xl md:text-3xl' : 'text-3xl md:text-4xl'} font-bold mb-3`}>
                Eligibility Criteria
              </h2>
              <p className={`${compact ? 'text-base' : 'text-lg'} text-purple-100 max-w-2xl mx-auto`}>
                C-CSP candidates must meet stringent requirements
              </p>
            </div>
            
            <div className={`grid ${compact ? 'md:grid-cols-2' : 'md:grid-cols-2'} gap-6`}>
              {eligibilityCriteria.map((criteria, index) => (
                <Card key={index} className="bg-white/10 backdrop-blur-sm border-white/20">
                  <CardContent className={compact ? "pt-4" : "pt-6"}>
                    <div className="flex items-start gap-4">
                      <div className="p-2 bg-white/20 rounded-lg">
                        {criteria.icon}
                      </div>
                      <div>
                        <h3 className={`${compact ? 'text-lg' : 'text-xl'} font-bold mb-2 text-white`}>{criteria.title}</h3>
                        <p className={`${compact ? 'text-sm' : 'text-base'} text-purple-100`}>{criteria.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Framework Implementation Requirements */}
      {showFramework && (
        <div className={`${compact ? 'py-8' : 'py-12'} bg-white`}>
          <div className={`max-w-${maxWidth} mx-auto px-4`}>
            <div className="text-center mb-8">
              <h2 className={`${compact ? 'text-2xl md:text-3xl' : 'text-3xl md:text-4xl'} font-bold text-gray-900 mb-3`}>
                Framework Implementation
              </h2>
              <p className={`${compact ? 'text-base' : 'text-lg'} text-gray-600 max-w-2xl mx-auto`}>
                Documentation required demonstrating leadership in key areas
              </p>
            </div>
            
            <div className={`grid ${compact ? 'md:grid-cols-1' : 'md:grid-cols-2'} gap-4`}>
              {frameworkRequirements.slice(0, compact ? 4 : 6).map((requirement, index) => (
                <Card key={index} className="border-purple-100 hover:border-purple-300 transition-colors">
                  <CardContent className={compact ? "pt-4" : "pt-6"}>
                    <div className="flex items-start gap-3">
                      <CheckCircle className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} text-purple-600 flex-shrink-0 mt-0.5`} />
                      <p className={`${compact ? 'text-sm' : 'text-base'} text-gray-700`}>{requirement}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Application Process */}
      {showApplication && (
        <div className={`${compact ? 'py-8' : 'py-12'} bg-gradient-to-br from-purple-50 to-indigo-50`}>
          <div className={`max-w-${maxWidth} mx-auto px-4`}>
            <div className="text-center mb-8">
              <h2 className={`${compact ? 'text-2xl md:text-3xl' : 'text-3xl md:text-4xl'} font-bold text-gray-900 mb-3`}>
                Application Process
              </h2>
              <p className={`${compact ? 'text-base' : 'text-lg'} text-gray-600`}>
                Rigorous evaluation ensuring highest standards
              </p>
            </div>
            
            <Card className="border-purple-200 shadow-lg">
              <CardContent className={compact ? "pt-6" : "pt-8"}>
                <div className={`grid ${compact ? 'grid-cols-2' : 'md:grid-cols-4'} gap-4 mb-6`}>
                  <div className="text-center">
                    <div className={`${compact ? 'w-8 h-8' : 'w-12 h-12'} bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2`}>
                      <span className={`text-purple-600 font-bold ${compact ? 'text-sm' : ''}`}>1</span>
                    </div>
                    <h3 className={`font-semibold mb-1 ${compact ? 'text-sm' : ''}`}>Eligibility Check</h3>
                    <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-600`}>Contact administration team</p>
                  </div>
                  <div className="text-center">
                    <div className={`${compact ? 'w-8 h-8' : 'w-12 h-12'} bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2`}>
                      <span className={`text-purple-600 font-bold ${compact ? 'text-sm' : ''}`}>2</span>
                    </div>
                    <h3 className={`font-semibold mb-1 ${compact ? 'text-sm' : ''}`}>Application</h3>
                    <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-600`}>Submit with €50 fee</p>
                  </div>
                  <div className="text-center">
                    <div className={`${compact ? 'w-8 h-8' : 'w-12 h-12'} bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2`}>
                      <span className={`text-purple-600 font-bold ${compact ? 'text-sm' : ''}`}>3</span>
                    </div>
                    <h3 className={`font-semibold mb-1 ${compact ? 'text-sm' : ''}`}>Review</h3>
                    <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-600`}>Up to 30 days evaluation</p>
                  </div>
                  <div className="text-center">
                    <div className={`${compact ? 'w-8 h-8' : 'w-12 h-12'} bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2`}>
                      <span className={`text-purple-600 font-bold ${compact ? 'text-sm' : ''}`}>4</span>
                    </div>
                    <h3 className={`font-semibold mb-1 ${compact ? 'text-sm' : ''}`}>Acceptance</h3>
                    <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-600`}>Final €450 fee</p>
                  </div>
                </div>
                
                {!compact && (
                  <div className="bg-purple-50 rounded-lg p-4 mb-6">
                    <h3 className="text-lg font-bold mb-3 text-purple-700">Fee Structure</h3>
                    <div className="grid md:grid-cols-3 gap-4 text-sm">
                      <div className="text-center">
                        <div className="text-xl font-bold text-purple-600">€50</div>
                        <div className="text-gray-600">Application Fee</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xl font-bold text-purple-600">€450</div>
                        <div className="text-gray-600">Final Fee</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xl font-bold text-purple-600">€500</div>
                        <div className="text-gray-600">Total Cost</div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Ideal Candidates */}
      {showIdealFor && (
        <div className={`${compact ? 'py-8' : 'py-12'} bg-white`}>
          <div className={`max-w-${maxWidth} mx-auto px-4`}>
            <div className="text-center mb-8">
              <h2 className={`${compact ? 'text-2xl md:text-3xl' : 'text-3xl md:text-4xl'} font-bold text-gray-900 mb-3`}>
                Perfect For
              </h2>
              <p className={`${compact ? 'text-base' : 'text-lg'} text-gray-600 max-w-2xl mx-auto`}>
                C-CSP is reserved for the most accomplished professionals
              </p>
            </div>
            
            <div className={`grid ${compact ? 'md:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-3'} gap-4`}>
              {idealFor.slice(0, compact ? 4 : 6).map((candidate, index) => (
                <Card key={index} className="border-purple-100 hover:border-purple-300 transition-colors">
                  <CardContent className={compact ? "pt-4" : "pt-6"}>
                    <div className="flex items-start gap-3">
                      <ArrowRight className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} text-purple-600 flex-shrink-0 mt-0.5`} />
                      <p className={`${compact ? 'text-sm' : 'text-base'} text-gray-700`}>{candidate}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Next Steps */}
      {showNextSteps && (
        <div className={`${compact ? 'py-8' : 'py-12'} bg-gradient-to-br from-purple-50 to-indigo-50`}>
          <div className={`max-w-${compact ? '3xl' : '4xl'} mx-auto px-4 text-center`}>
            <h2 className={`${compact ? 'text-2xl md:text-3xl' : 'text-3xl md:text-4xl'} font-bold text-gray-900 mb-4`}>
              Ready for Elite Recognition?
            </h2>
            <p className={`${compact ? 'text-base' : 'text-lg'} text-gray-600 mb-6`}>
              Join the exclusive ranks of Chartered Converged Security Practitioners
            </p>
            
            <Card className="border-purple-200 shadow-lg">
              <CardContent className={compact ? "pt-6" : "pt-8"}>
                <div className="mb-6">
                  <h3 className={`${compact ? 'text-lg' : 'text-xl'} font-bold mb-3 text-purple-700`}>Before You Apply</h3>
                  <p className={`${compact ? 'text-sm' : 'text-base'} text-gray-600 mb-4`}>
                    Check eligibility with our administration team before applying.
                  </p>
                  <div className="bg-purple-50 rounded-lg p-3">
                    <p className={`${compact ? 'text-xs' : 'text-sm'} text-purple-700 font-medium`}>
                      Contact: info@convergedsecurity.es for eligibility verification
                    </p>
                  </div>
                </div>
                
                <div className="flex flex-wrap justify-center gap-3">
                  <Button 
                    size={compact ? "default" : "lg"} 
                    className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                    onClick={() => window.open('https://pay.sumup.com/b2c/QVSOSICX', '_blank')}
                  >
                    <Star className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} mr-2`} />
                    Pay Application - €50
                  </Button>
                  <Button 
                    size={compact ? "default" : "lg"} 
                    className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                    onClick={() => window.open('https://pay.sumup.com/b2c/QMN044J9', '_blank')}
                  >
                    <Award className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} mr-2`} />
                    Pay Final - €450
                  </Button>
                  {!compact && (
                    <Button 
                      size="lg" 
                      variant="outline" 
                      className="border-purple-300 text-purple-600 hover:bg-purple-50"
                      onClick={() => window.open('mailto:info@convergedsecurity.es?subject=C-CSP Eligibility Check&body=Hello, I would like to check my eligibility for the C-CSP (Chartered Converged Security Practitioner) certification. I believe I meet the requirements and would like to discuss the application process. Please provide guidance on next steps.', '_blank')}
                    >
                      <Mail className="w-5 h-5 mr-2" />
                      Check Eligibility
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmbedCCSP;